import type { Notification } from '@/types/notification';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';

// -- Friend request Accept/Ignore buttons (rendered in the right column next to timestamp) --

interface FriendRequestButtonsProps {
  notification: Notification;
  canRespondToFriendRequest: boolean;
  onAcceptFriendRequest?: (notificationId: string, requestId: string) => void;
  onRejectFriendRequest?: (notificationId: string, requestId: string) => void;
  isProcessingFriendRequest: boolean;
}

export function FriendRequestButtons({
  notification,
  canRespondToFriendRequest,
  onAcceptFriendRequest,
  onRejectFriendRequest,
  isProcessingFriendRequest,
}: FriendRequestButtonsProps) {
  if (
    notification.type !== 'friend_request' ||
    !canRespondToFriendRequest ||
    notification.friendRequestAction
  ) {
    return null;
  }

  return (
    <View className="flex-row gap-2">
      <Pressable
        className="rounded-lg px-3 items-center justify-center bg-primary"
        style={{
          opacity: isProcessingFriendRequest ? 0.6 : 1,
          minHeight: 44,
        }}
        disabled={isProcessingFriendRequest}
        onPress={() => {
          if (onAcceptFriendRequest && notification.user?.id) {
            onAcceptFriendRequest(notification.id, notification.user.id);
          }
        }}
      >
        {isProcessingFriendRequest ? (
          <ActivityIndicator size="small" className="text-dim" />
        ) : (
          <Text className="font-medium text-sm text-primary-foreground">
            Accept
          </Text>
        )}
      </Pressable>
      <Pressable
        className="rounded-lg px-3 items-center justify-center bg-surface-secondary border border-border"
        style={{
          opacity: isProcessingFriendRequest ? 0.6 : 1,
          minHeight: 44,
        }}
        disabled={isProcessingFriendRequest}
        onPress={() => {
          if (onRejectFriendRequest && notification.user?.id) {
            onRejectFriendRequest(notification.id, notification.user.id);
          }
        }}
      >
        <Text className="font-medium text-sm text-muted-foreground">
          Ignore
        </Text>
      </Pressable>
    </View>
  );
}

// -- Actions rendered below the content area (status badges + follow actions) --

interface NotificationActionsProps {
  notification: Notification;
  canRespondToFriendRequest: boolean;
}

export function NotificationActions({
  notification,
  canRespondToFriendRequest,
}: NotificationActionsProps) {
  if (notification.type === 'friend_request') {
    return (
      <FriendRequestStatusBadge
        notification={notification}
        canRespond={canRespondToFriendRequest}
      />
    );
  }

  if (notification.type === 'follow') {
    return <FollowActions />;
  }

  return null;
}

// -- Private sub-components --

function FriendRequestStatusBadge({
  notification,
  canRespond,
}: {
  notification: Notification;
  canRespond: boolean;
}) {
  const hasAction = notification.friendRequestAction;
  const showBadge = hasAction || (!canRespond && !hasAction);

  if (!showBadge) return null;

  return (
    <View className="mt-2 flex-row items-center">
      <Ionicons
        name={
          hasAction === 'accepted'
            ? 'checkmark-circle'
            : hasAction === 'rejected'
              ? 'close-circle'
              : 'information-circle'
        }
        size={16}
        className={
          hasAction === 'accepted'
            ? 'text-primary'
            : 'text-muted-foreground'
        }
      />
      <Text
        className={`ml-2 text-sm ${
          hasAction === 'accepted'
            ? 'text-muted-foreground'
            : 'text-muted-foreground'
        }`}
      >
        {hasAction === 'accepted'
          ? 'Friend request accepted'
          : hasAction === 'rejected'
            ? 'Friend request ignored'
            : 'Friend request handled'}
      </Text>
    </View>
  );
}

function FollowActions() {
  return (
    <View className="flex-row mt-2">
      <Pressable
        className="rounded-full px-4 mr-2 items-center justify-center bg-primary"
        style={{ minHeight: 44 }}
      >
        <Text className="font-medium text-dim">
          Follow Back
        </Text>
      </Pressable>
      <Pressable
        className="rounded-full px-4 items-center justify-center bg-surface-hover"
        style={{ minHeight: 44 }}
      >
        <Text className="font-medium text-muted-foreground">
          Ignore
        </Text>
      </Pressable>
    </View>
  );
}
